import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import { fileUrl } from "./Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hardcoded "featured" photos used as the bottom row of the homepage preview.
// The top row pulls dynamically from the latest Shamanic album photos.
const FEATURED_BOTTOM = [
  { url: "/shamanic-preview-3.jpg", rotation: 0 },
  { url: "/shamanic-preview-4.jpg", rotation: 0 },
];

export const ShamanicPreview = () => {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const albums = await fetch(`${API}/albums`).then((r) => r.json());
        const shamanic = Array.isArray(albums)
          ? albums.find((a) => a.name === "Shamanic")
          : null;
        if (!shamanic) {
          if (active) setPhotos(FEATURED_BOTTOM);
          return;
        }
        const data = await fetch(
          `${API}/photos?limit=12&album_id=${shamanic.id}`
        ).then((r) => r.json());
        if (!active || !Array.isArray(data)) return;
        const top = data.slice(0, 2).map((p) => ({
          url: fileUrl(p.image_path),
          rotation: p.rotation || 0,
        }));
        setPhotos([...top, ...FEATURED_BOTTOM]);
      } catch {
        if (active) setPhotos(FEATURED_BOTTOM);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="shamanic-preview"
      data-testid="shamanic-preview-section"
      className="relative z-10 py-28 md:py-36 px-6"
      style={{
        background:
          "radial-gradient(900px 500px at 80% 20%, rgba(122,158,109,0.10), transparent 65%)",
      }}
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        {/* LEFT: text */}
        <motion.div
          initial={{ opacity: 0, x: -28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
        >
          <div className="inline-flex items-center gap-3 mb-7 text-[#7A9E6D] text-outline-strong">
            <Leaf size={16} />
            <span className="text-xs font-bold uppercase tracking-[0.35em]">
              Shamanic Path
            </span>
          </div>
          <h2 className="font-heading font-light text-4xl md:text-6xl text-[#EBE4D8] leading-[1.05] mb-7 text-outline-strong">
            The shaman <span className="italic text-[#D6B56D]">way</span>
          </h2>
          <p className="text-[#EBE4D8] text-base md:text-lg font-light leading-relaxed mb-10 text-outline-strong">
            I lead a double life. One bound by paying taxes, and another deep within the fascinating world of shamanism. When I am not in one, I am in the other. I am being initiated by Peruvian shamans into the medicine of sacred plants like Ayahuasca, Rapé, Mapacho, and constantly learning how complex, yet beautiful, this world truly is. Shamanism is a path of love, a path of reverence for all of God's creation. That is what won my heart and this is the path I'm holding now: healing, presence, and a gentle reconnection with nature, with the ancestors and...my own heart.
          </p>

          <Link
            to="/shamanic-path"
            data-testid="shamanic-preview-cta"
            className="inline-flex items-center gap-3 px-9 py-3.5 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all duration-300"
          >
            See more <ArrowRight size={16} />
          </Link>
        </motion.div>

        {/* RIGHT: 4-photo preview (or fallback card) */}
        <motion.div
          initial={{ opacity: 0, x: 28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="relative"
        >
          <div className="absolute -inset-6 rounded-[2.5rem] bg-[#7A9E6D]/10 blur-3xl" />

          {photos.length > 0 ? (
            <Link
              to="/shamanic-path"
              data-testid="shamanic-preview-photos"
              className="relative grid grid-cols-2 gap-3 md:gap-4 group/grid w-full"
            >
              {photos.map((p, i) => {
                const r = p.rotation || 0;
                const isSide = r === 90 || r === 270;
                return (
                  <div
                    key={`${p.url}-${i}`}
                    data-testid={`shamanic-preview-photo-${i}`}
                    className="relative aspect-square rounded-2xl overflow-hidden border border-[#D6B56D]/25 hover:border-[#D6B56D]/60 transition-colors"
                  >
                    <img
                      src={p.url}
                      alt=""
                      loading="lazy"
                      style={r ? { transform: `rotate(${r}deg)` } : undefined}
                      className={`absolute inset-0 w-full h-full ${
                        isSide ? "object-contain" : "object-cover"
                      } transition-transform duration-700 group-hover/grid:scale-[1.02]`}
                    />
                  </div>
                );
              })}
            </Link>
          ) : (
            <div className="relative h-[360px] md:h-[480px] rounded-[2rem] border border-[#D6B56D]/25 bg-gradient-to-br from-[#0F1410]/80 to-[#06040A]/80 backdrop-blur-xl flex flex-col items-center justify-center text-center px-10">
              <Leaf size={46} className="text-[#7A9E6D] mb-6" />
              <p className="font-heading italic text-3xl md:text-4xl text-[#EBE4D8] mb-4 leading-tight">
                A path of <span className="text-[#D6B56D]">love</span>
              </p>
              <p className="text-[#A59DAB] font-light max-w-xs leading-relaxed">
                Reverence for all of creation, learned slowly — under the wing of Peruvian elders.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
